/* One weekday column, transcribed from `Builder planu - tydzien - programator.dc.html`'s `d.col`,
 * `d.dowSt`, `d.minSt`, `d.cardSt`, `d.emptySt` and `d.emptyLabel`.
 *
 * ── `--text-3` IS used for text here now ─────────────────────────────────────────────────────
 * This file used to open with a note refusing KANON's tertiary ink on contrast grounds and carrying
 * the mock's three-step hierarchy as a two-step one. The owner rejected the result beside the mock
 * on 8 September 2026. A rest day's weekday is `--text-3` and the duration line is `--text-3`,
 * because that is what the mock writes. The shortfall is real (2.80:1 on a white card, 4.02:1 on a
 * night one) and is the designer's to answer; nothing here is told apart by it.
 *
 * ── The header does NOT stick ────────────────────────────────────────────────────────────────
 * It was `position: sticky` with an opaque `--bg` ground, put there because a short window scrolled
 * every weekday label out of the scrollport at once. The mock's grid is `overflow-y: hidden` — a
 * day card's own body is what scrolls — so the case cannot arise, and a sticky row with a painted
 * ground is a thing the mock does not draw. Both are gone.
 *
 * ── The card box is HERE, not on the session card ────────────────────────────────────────────
 * The mock draws ONE box per column (`cardSt`) holding two absolutely-positioned layers that cross
 * fade: `emptySt` (the + tile) and `bodySt` (the workout). The box owns the fill, the edge, the
 * radius, the shadow and every state; the workout inside it is transparent. So `.card` below is the
 * mock's `cardSt` and `SessionCard` renders the mock's `bodySt`.
 *
 * The drag state reaches the box through `:has()` rather than through a prop threaded back up: the
 * session card already sets `data-dragging` and `data-lifted` on its own layer, and the box is its
 * parent. */

/* ── The column, and the one animated property this slice argues for ───────────────────────────
 * `CLAUDE.md` forbids animating a property that lays the page out again, and `flex-basis` /
 * `flex-grow` is exactly that. It stays, and the exception is deliberate and narrow: the mock
 * declares `transition: flex-grow .42s cubic-bezier(.2,.8,.3,1), flex-basis .42s …` on every column
 * and the widening IS the gesture — a rest day becoming a training day is a column growing from
 * 84px to its share. It can fire on nothing else (only adding or removing a day and switching phase
 * touch either property) and it cannot fire on first paint, because a transition animates a change
 * from a previously rendered value and a freshly mounted element has none. */
.column {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  min-height: 0;
  transition:
    flex-grow var(--dur-grid-flex) var(--ease-fp),
    flex-basis var(--dur-grid-flex) var(--ease-fp),
    opacity var(--dur-fade) var(--ease-default);
}

/* A programmed day: grows into whatever the rest days leave over, from a 232px basis, capped at
 * 496px so a single training in a week does not become a document.
 *
 * ── `flex-shrink` is 1, and that is one of the TWO permitted departures from the mock ────────
 * The mock declares `flexShrink: 0`, so seven trainings at their 232px basis overflow the canvas.
 * `KANON - builder planow` states the rule the mock's own numbers contradict — "dni treningowe
 * dzielą resztę po równo — tydzień zawsze mieści się na ekranie" — and the owner settled it in
 * KANON's favour on 7 September 2026. 232px is a preferred size, not a floor. */
.training {
  flex: 1 1 var(--w-day-train);
  max-width: var(--w-day-max);
}

/* An unprogrammed day: a fixed 84px strip. It neither grows nor shrinks — the whole point of the
 * layout is that the room a rest day does not need goes to the trainings. */
.rest {
  flex: 0 0 var(--w-day-rest);
}

/* The mock's header: two centred lines 1px apart, `padding: 0 2px`, clipping rather than wrapping.
 * It is OUTSIDE the card — the header belongs to the week and the card is what moves. */
.head {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1px);
  align-items: center;
  padding: var(--space-0) var(--space-2px);
  overflow: hidden;
}

/* Three steps by state, exactly as `dowSt` writes them: `--text-3` on a rest day, `--text-2` on a
 * programmed one, `--text` while the column is a drop target. */
.weekday {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
  color: var(--text-3);
  white-space: nowrap;
  transition-property: color;
  transition-duration: var(--dur-base);
  transition-timing-function: var(--ease-default);
}

.training .weekday {
  color: var(--text-2);
}

.column[data-target] .weekday {
  color: var(--text);
}

/* "~62 min", or nothing at all.
 *
 * **The line is NOT reserved on a rest day, and that is the mock's own layout.** This carried
 * `min-height: 1lh` so every column head was 26px and every card started at the same y. Measured on
 * 8 September 2026 the mock does the opposite: its `minSt` is an empty span at `opacity: 0`, which
 * is zero pixels tall, so a rest column's head is 14px and its dashed box starts 12px HIGHER than
 * the workout cards beside it. That step is visible in the mock's own render and is what ships.
 *
 * Nothing moves as a result: a day that gains a duration gains it together with its card, and the
 * fade is the mock's `opacity .3s`. */
.duration {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  white-space: nowrap;
  opacity: 0;
  font-variant-numeric: tabular-nums;
  transition-property: opacity;
  transition-duration: var(--dur-fade);
  transition-timing-function: var(--ease-default);
}

.training .duration {
  opacity: 1;
}

/* ── The card box: the mock's `cardSt` ─────────────────────────────────────────────────────────
 * A rest day is a dashed `--border-2` box over nothing; a training day is `--bg-card` under a solid
 * `--border` hairline with the card shadow. Radius 14 either way, so the two states are one box
 * changing rather than two boxes swapping.
 *
 * The transition is the mock's, property for property: `background .42s cubic-bezier(.2,.8,.3,1),
 * border-color .18s ease, box-shadow .18s ease, opacity .18s ease, transform .18s ease`. Two
 * durations, so they are declared in two blocks. */
.card {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);

  /* The mock's own five, property for property: `background .42s cubic-bezier(.2,.8,.3,1),
   * border-color .18s ease, box-shadow .18s ease, opacity .18s ease, transform .18s ease`.
   *
   * Written as the `transition` SHORTHAND because two different durations are needed and
   * `transition-duration` is on Stylelint's value allowlist as a single token — a comma list fails
   * it. Every value below is still a token; nothing rides in as a literal. */
  transition:
    background var(--dur-grid-flex) var(--ease-fp),
    border-color var(--dur-fp-fade) var(--ease-default),
    box-shadow var(--dur-fp-fade) var(--ease-default),
    opacity var(--dur-fp-fade) var(--ease-default),
    transform var(--dur-fp-fade) var(--ease-default);
}

.training .card {
  cursor: grab;
  background: var(--bg-card);
  border-style: solid;
  border-color: var(--border);
  box-shadow: var(--shadow-card);
}

/* The drag SOURCE dims and shrinks in place. `:has()` rather than a prop threaded back up from the
 * card: the card already carries `data-dragging` on its own layer and the box is its parent. */
.card:has([data-dragging]) {
  opacity: 0.4;
  transform: scale(0.97);
}

/* The DROP target. Every ink in it is neutral — `--bg-inset` under a `--text` edge and a 3px ring in
 * translucent ink. An OCCUPIED target keeps `--bg-card`, which is what the mock's own
 * `has ? '--bg-card' : target ? '--bg-inset' : 'transparent'` says.
 *
 * `data-target` is set by the drop adapter while the pointer is over the box, and by the pane while
 * a KEYBOARD lift points at this column — so a sighted keyboard user gets the same picture as a
 * sighted pointer user. */
.column[data-target] .card {
  background: var(--bg-inset);
  border-color: var(--text);
  box-shadow: var(--shadow-drop-ring);
}

.column[data-target].training .card {
  background: var(--bg-card);
}

/* The SELECTED day — the one whose panel is open. The mock's `active`: the accent on the edge and
 * a 3px `rgba(255,90,30,.16)` ring. It loses to `data-target`, exactly as the mock's own ternary
 * does, because a drop about to land is the more urgent of the two. */
.card[data-selected] {
  border-color: var(--accent);
  box-shadow: var(--shadow-select-ring);
}

.column[data-target] .card[data-selected] {
  border-color: var(--text);
  box-shadow: var(--shadow-drop-ring);
}

/* ── The EMPTY layer: the mock's `emptySt` ─────────────────────────────────────────────────────
 * Absolutely positioned over the box, centred, fading out over `.25s` when a workout arrives. It is
 * always mounted — that is what makes the cross-fade possible — and `inert` while a workout stands
 * over it, so it takes neither focus nor a press. */
.empty {
  position: absolute;
  inset: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  transition-property: opacity;
  transition-duration: var(--dur-empty-fade);
  transition-timing-function: var(--ease-default);
}

.training .empty {
  opacity: 0;
  pointer-events: none;
}

/* The reason the tile refuses, standing under it.
 *
 * It was a full sentence in a row above the canvas until 16 September 2026, and that row appearing
 * on a phase switch moved the whole week down 31.3px (measured). The reason belongs at the control
 * anyway, so it came down here — but an empty weekday column is about 80px wide, which is two short
 * words and no more. The whole sentence is still carried, on the tile's tooltip and its description;
 * this is the visible mark that the column is refused.
 *
 * `--text-2`, not the `--text-3` the dimmed tile above it wears: the tile is quiet because it cannot
 * act, and this is the one line that has to be READ. */
.refusal {
  max-width: 100%;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: center;
  text-wrap: balance;
}

/* The press target is the whole stack — the tile AND the word — which is what the mock's own
 * `onClick` on `emptySt` amounts to. It draws nothing of its own. */
.addDay {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-0);
  font-family: var(--font-body);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.addDay:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-sm);
}

/* The mock's 26×26 radius-9 tile carrying the `+`, at 15px/700 on `--bg-chip`. */
.emptyTile {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  font-size: var(--fs-session);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

.emptyLabel {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
}

/* Refused. See the block under the hover pair for why this stopped keeping the tile's full ink. */
.addDay[aria-disabled="true"] .emptyTile,
.addDay[aria-disabled="true"] .emptyLabel {
  color: var(--text-3);
}

.addDay:hover:not([aria-disabled="true"]) .emptyTile,
.addDay:hover:not([aria-disabled="true"]) .emptyLabel {
  color: var(--text);
}

/* A phase the client has already lived through every cycle of takes no new training, and the tile
 * says so before it is pressed.
 *
 * It KEPT its ink until 16 September 2026, on the reasoning that the sentence above the canvas was
 * the explanation. That left a refused tile pixel-identical to a working one in six other columns,
 * so the only way to learn it was refused was to press it — and the press answered with nothing.
 * It drops to `--text-3` now, which is exactly what a refused `HeaderAction` on the card beside it
 * does: the control still reads as the same control, one weight quieter. */
.addDay[aria-disabled="true"] {
  cursor: not-allowed;
}
